// The one file in Scheduling V3 allowed to touch sessionStorage. ENG-2047 AC 8 / Chen,
// 2026-09-23: a read-through cache of the settings document, session-lifetime only.
// localStorage and indexedDB stay banned everywhere, including here. The ESLint exception
// that permits this is a single-file override in the root `.eslintrc.json`, immediately
// after the module-wide ban, and `settings-cache.test.ts` fails if its blast radius grows.
import { datadogRum } from "@datadog/browser-rum";

import { SETTINGS_DOCUMENT_VERSION } from "../domain/settings-document";

/**
 * Why a cache at all: without one, a returning user waits up to three seconds on the
 * server read before the grid can be laid out, and then watches it re-fold. A hit paints
 * the stored layout on the first frame and releases the hydration gate at once; the server
 * read still lands and still wins (silently inside the cap, through the notice after it).
 *
 * What it may hold: the settings document only — clinic, provider and appointment-type
 * **ids**, view flags and minute offsets. No events, no appointments, no patient anything,
 * no names, no token, no catalog. That is why a session lifetime is enough, and why it is
 * `sessionStorage` rather than `localStorage`: exam-room computers are shared.
 */

/** Every key the sweep owns. `c1` is the CACHE schema, not the document's `v`. */
export const SCHEDULING_SETTINGS_CACHE_PREFIX = "suno.scheduling-settings.";
const CACHE_SCHEMA = 1;

/** The server refuses a document this large, so the cache never stores one either. */
const MAX_ENTRY_BYTES = 65_536;

export interface SchedulingSettingsCacheEntry {
  /** The cache schema. A reshape bumps the key prefix; the sweep clears the old one. */
  c: typeof CACHE_SCHEMA;
  /** The document's own version, so a newer build's document is ignored, not misread. */
  v: number;
  document: unknown;
  updated_at: string;
}

/**
 * Keyed by user AND practice. AC 8 says "keyed by user id"; the `scheduler` document is
 * per (user, practice), so a user-id-only key would serve practice A's layout inside
 * practice B (ENG-2047 D16).
 */
export function schedulingSettingsCacheKey({
  userId,
  practiceId
}: {
  userId: number;
  practiceId: number | null;
}): string {
  return `${SCHEDULING_SETTINGS_CACHE_PREFIX}c${CACHE_SCHEMA}.${userId}.${practiceId ?? "none"}`;
}

/**
 * Every access is wrapped: private mode, a disabled-storage policy and a full origin all
 * throw, and each one has to read as a plain cache miss. The RUM counter is not optional —
 * a silent `catch {}` is how a full origin gets blamed on whichever namespace happens to
 * be noisy (see the repo CLAUDE.md note on `QuotaExceededError`).
 */
function countUnavailable(op: "read" | "write" | "sweep"): void {
  datadogRum.addAction("scheduling_settings_cache_unavailable", { op });
}

const isEntry = (raw: unknown): raw is SchedulingSettingsCacheEntry => {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return false;
  const candidate = raw as Record<string, unknown>;
  return (
    candidate.c === CACHE_SCHEMA &&
    Number.isInteger(candidate.v) &&
    typeof candidate.updated_at === "string" &&
    typeof candidate.document === "object" &&
    candidate.document !== null &&
    !Array.isArray(candidate.document)
  );
};

/** `null` is a miss, whatever the reason. The caller then waits for the server. */
export function readSchedulingSettingsCache(
  key: string
): SchedulingSettingsCacheEntry | null {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(key);
  } catch {
    countUnavailable("read");
    return null;
  }
  if (stored === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    parsed = undefined;
  }

  if (!isEntry(parsed)) {
    // Unreadable: drop it rather than re-answer with it on every mount.
    try {
      sessionStorage.removeItem(key);
    } catch {
      countUnavailable("read");
    }
    return null;
  }

  // A document from a newer build is ignored, NOT deleted — the tab that wrote it still
  // owns it, and the server read applies the version hold anyway.
  if (parsed.v > SETTINGS_DOCUMENT_VERSION) return null;

  return parsed;
}

/**
 * Called on every successful GET, every successful PUT response and on Apply — never on a
 * local change alone. The cache mirrors what the SERVER has, so a failed write can never
 * leave a lie behind that outlives the tab's Redux state.
 */
export function writeSchedulingSettingsCache(
  key: string,
  entry: SchedulingSettingsCacheEntry
): void {
  const serialized = JSON.stringify(entry);
  if (serialized.length > MAX_ENTRY_BYTES) {
    datadogRum.addAction("scheduling_settings_cache_oversized", {
      op: "write"
    });
    return;
  }
  try {
    sessionStorage.setItem(key, serialized);
  } catch {
    countUnavailable("write");
  }
}

/**
 * Runs at every hydration and leaves at most one entry: the current key. That is what
 * covers a user switch, a practice switch, a logout and a cache-schema bump without
 * anything having to call in from outside V3 — coupling `use-switch-practice` to this
 * module was rejected (D12).
 */
export function sweepSchedulingSettingsCache(currentKey: string): void {
  try {
    const stale: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (
        key &&
        key !== currentKey &&
        key.startsWith(SCHEDULING_SETTINGS_CACHE_PREFIX)
      ) {
        stale.push(key);
      }
    }
    // Collected first: removing inside the loop re-indexes the keys underneath it.
    stale.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    countUnavailable("sweep");
  }
}
